import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './RaffleScreen.css';

/* The live raffle on the big screen — a beamer at the event, reached by a PUBLIC address.

   ★★ No login. The token in the address is the key; Machina (Operations ▸ Raffle ▸ Draw) shows the
   link and can rotate it. Machina does the drawing — the server picks the winner — and this page
   only performs it: a reel of the pot's faces and names that slows down and lands on the winner,
   then the winner with the prize.

   ★ Polls every 2 s. A NEW win id is what starts the reel; on first load the current win is shown
   straight away, so reopening the screen never replays a draw. "Draw again" in Machina moves the
   current win on in the same press — the no-show is spotted in `recent` and gets a short "not
   here" moment before the reel runs again.

   Same object as the house board: no nav, everything in `vh`, F for fullscreen. */

const POLL_MS = 2000;
const NOT_HERE_MS = 3000;
// The reel: ~32 flips whose gaps grow from fast to slow — about six seconds in all.
const REEL_STEPS = 32;
const reelDelay = (i) => 55 + Math.round(420 * Math.pow(i / REEL_STEPS, 2.4));

const cssUrl = (image) => (
  typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

const initials = (p) => `${(p?.first_name || '?')[0] || ''}${(p?.last_name || '')[0] || ''}`.toUpperCase();

const money = (value, currency) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  const shown = Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return currency ? `${currency} ${shown}` : shown;
};

const Face = ({ person, fallback, className }) => {
  const photo = person?.photo_url || fallback;
  return photo
    ? <span className={className} style={{ backgroundImage: `url("${cssUrl(photo)}")` }} />
    : <span className={`${className} rs-face--initials`}>{initials(person)}</span>;
};

const CONFETTI = Array.from({ length: 90 }, (_, i) => ({
  left: Math.random() * 100,
  delay: Math.random() * 2.2,
  duration: 3 + Math.random() * 2.5,
  size: 0.6 + Math.random() * 0.9,
  hue: i % 4,
}));

const Confetti = () => (
  <div className="rs-confetti" aria-hidden="true">
    {CONFETTI.map((c, i) => (
      <span key={i} className={`rs-confetti__bit rs-confetti__bit--${c.hue}`}
            style={{ left: `${c.left}%`, animationDelay: `${c.delay}s`,
                     animationDuration: `${c.duration}s`, width: `${c.size}vh`, height: `${c.size * 1.6}vh` }} />
    ))}
  </div>
);

const RaffleScreen = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [gone, setGone] = useState(false);
  // What is on screen: 'idle' | 'notHere' | 'spinning' | 'reveal'
  const [phase, setPhase] = useState('idle');
  const [shown, setShown] = useState(null);       // the win being performed / revealed
  const [reelFace, setReelFace] = useState(null); // the face the reel is on right now
  const [noShow, setNoShow] = useState('');
  const seenRef = useRef(undefined);              // last win id we started a performance for
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const perform = useCallback((state, prevId) => {
    const win = state.current;
    clearTimers();
    const prev = (state.recent || []).find((r) => r.win_id === prevId);
    const pool = (state.reel || []).length ? state.reel : [win.winner];

    const spin = () => {
      setPhase('spinning');
      setShown(win);
      let at = 0;
      for (let i = 0; i < REEL_STEPS; i += 1) {
        at += reelDelay(i);
        const face = i === REEL_STEPS - 1 ? win.winner : pool[(i * 7 + 3) % pool.length];
        timers.current.push(setTimeout(() => setReelFace(face), at));
      }
      timers.current.push(setTimeout(() => setPhase('reveal'), at + 700));
    };

    if (prev && prev.status === 'forfeited') {
      setNoShow(prev.first_name || '');
      setPhase('notHere');
      timers.current.push(setTimeout(spin, NOT_HERE_MS));
    } else {
      spin();
    }
  }, []);

  const load = useCallback(() => {
    apiRequest('GET', `/public/raffle/${token}`)
      .then((res) => {
        setData(res);
        setGone(false);
        const id = res.current ? res.current.win_id : null;
        if (seenRef.current === undefined) {
          // First load: show where the draw is, never replay it.
          seenRef.current = id;
          if (res.current) { setShown(res.current); setPhase('reveal'); }
          return;
        }
        if (id && id !== seenRef.current) {
          const prevId = seenRef.current;
          seenRef.current = id;
          perform(res, prevId);
        } else if (id) {
          // Same win, maybe a new status (collected): keep the stage, refresh its data.
          setShown((s) => (s && s.win_id === id ? res.current : s));
        }
      })
      // ★ A failed poll keeps the screen as it is; only a link that never loaded says it is gone.
      .catch(() => setGone(true));
  }, [token, perform]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const toggleFull = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'f' || e.key === 'F') toggleFull(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFull]);

  const event = data?.event || {};
  const accent = event.primary_color || '#e32574';
  const fallback = event.default_player_photo;
  const prizes = useMemo(() => data?.prizes || [], [data]);
  const left = useMemo(() => prizes.reduce((n, p) => n + (p.left || 0), 0), [prizes]);
  const winner = shown?.winner;
  const prize = shown?.prize;

  return (
    <div className="rs" style={{ '--rs-accent': accent }}>
      <div className="rs__head">
        {event.logo && <span className="rs__logo" style={{ backgroundImage: `url("${cssUrl(event.logo)}")` }} />}
        <span className="rs__title">
          {event.name && <span className="rs__event">{event.name}</span>}
          {t('raffleScreenTitle')}
        </span>
        {data && <span className="rs__left">{t('raffleScreenLeft', { count: left })}</span>}
      </div>

      <div className="rs__stage">
        {gone && !data && <p className="rs__note">{t('raffleScreenGone')}</p>}
        {!gone && !data && <p className="rs__note">{t('pleaseWait')}</p>}

        {data && phase === 'idle' && (
          prizes.length === 0
            ? <p className="rs__note">{t('raffleScreenNoPrizes')}</p>
            : (
              <div className="rs-wall">
                <h2 className="rs-wall__title">{t('raffleScreenToWin')}</h2>
                <div className="rs-wall__grid">
                  {prizes.map((p, i) => (
                    <div key={i} className={`rs-prize${p.left ? '' : ' rs-prize--gone'}`}>
                      {p.photo_url
                        ? <span className="rs-prize__img" style={{ backgroundImage: `url("${cssUrl(p.photo_url)}")` }} />
                        : <span className="rs-prize__img rs-prize__img--none"><Icon name="gift" theme="light" /></span>}
                      <span className="rs-prize__name">{p.quantity > 1 ? `${p.quantity}× ` : ''}{p.name}</span>
                      {p.sponsor && <span className="rs-prize__sponsor">{p.sponsor}</span>}
                      {money(p.value, event.currency) && <span className="rs-prize__value">{money(p.value, event.currency)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )
        )}

        {phase === 'notHere' && (
          <div className="rs-nothere">
            <p className="rs-nothere__line">{t('raffleScreenNotHere', { name: noShow })}</p>
            <p className="rs-nothere__sub">{t('raffleScreenDrawingAgain')}</p>
          </div>
        )}

        {phase === 'spinning' && (
          <div className="rs-reel">
            <p className="rs-reel__for">{t('raffleScreenDrawingFor')} <b>{prize?.name}</b></p>
            <Face person={reelFace} fallback={fallback} className="rs-face rs-face--reel" />
            <p className="rs-reel__name">{reelFace ? `${reelFace.first_name} ${reelFace.last_name}` : '…'}</p>
          </div>
        )}

        {phase === 'reveal' && winner && (
          <div className="rs-reveal">
            {shown.status === 'drawn' && <Confetti />}
            <div className="rs-reveal__person">
              <p className="rs-reveal__kicker">{t('raffleScreenWinner')}</p>
              <span className="rs-reveal__facewrap">
                <Face person={winner} fallback={fallback} className="rs-face rs-face--winner" />
                {shown.status === 'collected' && <span className="rs-stamp">{t('raffleScreenCollected')}</span>}
              </span>
              <p className="rs-reveal__name">{winner.first_name} {winner.last_name}</p>
              {winner.team_name && <p className="rs-reveal__team">{winner.team_name}</p>}
              {shown.status === 'drawn' && <p className="rs-reveal__come">{t('raffleScreenComeNow')}</p>}
            </div>
            <div className="rs-reveal__prize">
              {prize?.photo_url
                ? <span className="rs-reveal__img" style={{ backgroundImage: `url("${cssUrl(prize.photo_url)}")` }} />
                : <span className="rs-reveal__img rs-prize__img--none"><Icon name="gift" theme="light" /></span>}
              <p className="rs-reveal__prizename">{prize?.name}</p>
              {prize?.sponsor && <p className="rs-reveal__sponsor">{t('raffleScreenBy', { sponsor: prize.sponsor })}</p>}
              {money(prize?.value, prize?.currency) && <p className="rs-reveal__value">{money(prize.value, prize.currency)}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="rs__bar">
        {phase === 'reveal' && (
          <button type="button" className="rs__btn" onClick={() => setPhase('idle')} title={t('raffleScreenShowPrizes')}>
            <Icon name="apps" theme="light" />
          </button>
        )}
        <button type="button" className="rs__btn" onClick={toggleFull} title={t('rankingScreenFullscreen')}>
          <Icon name="expand" theme="light" />
        </button>
      </div>
    </div>
  );
};

export default RaffleScreen;
